"use client";

/**
 * App-wide host for the $1 trial offer.
 *
 * <TrialDismissFallback/> — the component that actually listens for
 * `panda:trial-fallback` and renders the modal — used to be mounted in exactly
 * one place: app/project/[id]/page.tsx. So even after an upgrade entry point on
 * the dashboard, in Settings, or on the credit chip decided to show the $1
 * offer, the event it dispatched had no listener and nothing happened. Mounting
 * the host at the root fixes that for every route at once.
 *
 * It also owns the eligibility PREFETCH. The decision at click time has to be
 * effectively synchronous or the user sees the plans modal flash before the $1
 * modal replaces it, so the answer must already be in memory by the time they
 * press anything (lib/trial/upgrade-entry-client.ts).
 *
 * IMPORTANT: exactly one of these may be mounted. Two listeners would open two
 * modals from one dispatch — which is the "no entry point shows conflicting
 * upgrade modals" rule, broken in the most literal way. The project page's own
 * mount was removed when this was added.
 */

import { useEffect } from "react";

import { TrialDismissFallback } from "@/components/trial/trial-dismiss-fallback";
import { TrialConvertHost } from "@/components/trial/trial-convert-host";
import {
  prefetchUpgradeEntryEligibility,
  invalidateUpgradeEntryEligibility,
} from "@/lib/trial/upgrade-entry-client";

export function TrialOfferHost() {
  useEffect(() => {
    let cancelled = false;
    void prefetchUpgradeEntryEligibility(() => cancelled);

    // Re-read when the user comes back to the tab. The cached answer is a claim
    // about the account, and accounts change elsewhere: a plan bought in another
    // tab, or a trial started and then browser-back'd, would otherwise leave a
    // stale "eligible" behind and show the $1 modal to someone the server will
    // refuse — a modal that looks broken.
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      invalidateUpgradeEntryEligibility();
      void prefetchUpgradeEntryEligibility(() => cancelled);
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  // Two listeners, two populations, and they are mutually exclusive by
  // construction: TrialDismissFallback answers TRIAL_OPEN_EVENT (a FREE,
  // eligible user being offered the $1 trial), TrialConvertHost answers
  // TRIAL_CONVERT_EVENT (a user already INSIDE that trial). The router
  // dispatches exactly one of the two per click, so they can never both open.
  //
  // Mounted here rather than separately in the layout so the "exactly one host"
  // rule stays enforced in one place for both.
  return (
    <>
      <TrialDismissFallback />
      <TrialConvertHost />
    </>
  );
}
